import { useContext, useEffect, useState } from "react";
import {
  GetTemplateContentRequest,
  GetTemplateDetailsRequest,
  GetTemplatesRequest,
  TemplateOptions,
  getTemplateContent,
  getTemplateDetails,
  getTemplates
} from "@coxauto-ui/cxm/communications/api";
import { ConversationTypeEnum } from "@coxauto-ui/cxm/communications/interfaces";
import { ConversationContext, ConversationDetailsContext } from "../../context";

export interface UseTemplatesParams {
  /** Setter for reply text (body) - used by all channels */
  setReplyText: (text: string) => void;
  /** Setter for email subject - email only */
  setReplyTextSubject?: (text: string) => void;
  /** Setter for email HTML head - email only */
  setTemplateHead?: (head: string | null) => void;
  /** Current template load state - tracks which template is loaded */
  selectedTemplateLoadState: {
    hasLoaded: boolean;
    loadedTemplateId: string | null;
  };
  /** Setter for template load state */
  setSelectedTemplateLoadState: (state: {
    hasLoaded: boolean;
    loadedTemplateId: string | null;
  }) => void;
}

export function useTemplates(params: UseTemplatesParams) {
  const {
    setReplyText,
    setReplyTextSubject,
    setTemplateHead,
    selectedTemplateLoadState,
    setSelectedTemplateLoadState
  } = params;

  const [templateData, setTemplateData] = useState<TemplateOptions>({});
  const [showTokenReplacementWarning, setShowTokenReplacementWarning] =
    useState(false);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [isInsertingTemplate, setIsInsertingTemplate] = useState(false);

  const {
    vinDealerId,
    env,
    customerSystem,
    customerId,
    autoLeadId,
    templateId,
    isGenerativeAIEnabled,
    defaultTab
  } = useContext(ConversationContext);

  // Get routing context
  const { currentConversationType, setShowGenerativeAI, setIsSuggestEdit } =
    useContext(ConversationDetailsContext);

  // Channel-aware function to set template content
  const setTemplateContent = (
    body: string,
    subject?: string,
    head?: string | null
  ) => {
    // Always set body text
    setReplyText(body);

    // Email: set subject and HTML head if setters are provided
    if (setReplyTextSubject && setTemplateHead) {
      setReplyTextSubject(subject || "");
      setTemplateHead(head || null);
    }
    // Text: only body is set (no subject or HTML head)
  };

  useEffect(() => {
    const fetchTemplates = async () => {
      if (
        !vinDealerId ||
        !currentConversationType ||
        ![ConversationTypeEnum.EMAIL, ConversationTypeEnum.TEXT].includes(
          currentConversationType
        )
      ) {
        return;
      }

      try {
        setIsLoadingTemplates(true);
        const request: GetTemplatesRequest = {
          dealerId: vinDealerId,
          conversationType: currentConversationType
        };

        const templates = await getTemplates(request, env);

        // Filter out GenAI templates if GenAI is not enabled
        const filteredTemplates = isGenerativeAIEnabled
          ? templates
          : Object.entries(templates).reduce(
              (acc, [category, templateList]) => {
                const nonGenAITemplates = templateList.filter(
                  template => !template.isGenAITemplate
                );
                if (nonGenAITemplates.length > 0) {
                  acc[category] = nonGenAITemplates;
                }
                return acc;
              },
              {} as TemplateOptions
            );

        setTemplateData(filteredTemplates);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch templates:", error);
        setTemplateData({});
      } finally {
        setIsLoadingTemplates(false);
      }
    };

    fetchTemplates();
  }, [currentConversationType, vinDealerId, env, isGenerativeAIEnabled]);

  // Load initial template when templateId is provided from context
  // This should only run ONCE when the component first mounts with a templateId
  useEffect(() => {
    const loadInitialTemplate = async () => {
      if (
        !templateId ||
        !vinDealerId ||
        !defaultTab ||
        !currentConversationType ||
        ![ConversationTypeEnum.EMAIL, ConversationTypeEnum.TEXT].includes(
          currentConversationType
        ) ||
        // Only load if current conversation type matches the default/initial tab
        // This prevents templates from loading into wrong contexts when switching tabs
        (defaultTab && currentConversationType !== defaultTab) ||
        (selectedTemplateLoadState.hasLoaded &&
          selectedTemplateLoadState.loadedTemplateId === templateId)
      ) {
        return;
      }

      try {
        // Mark as loaded immediately to prevent duplicate calls
        setSelectedTemplateLoadState({
          hasLoaded: true,
          loadedTemplateId: templateId
        });
        setIsInsertingTemplate(true);

        const request: GetTemplateDetailsRequest = {
          dealerId: vinDealerId,
          conversationType: currentConversationType,
          templateId: parseInt(templateId, 10),
          consumerId: customerId,
          consumerRecordSystem: customerSystem
        };

        if (autoLeadId) {
          request.leadId = parseInt(autoLeadId, 10);
        }

        const response = await getTemplateDetails(request, env);
        if (response && Object.keys(response).length > 0) {
          // Load template content using channel-aware setter
          setTemplateContent(
            response.body || "",
            response.subject,
            response.head
          );
          setShowTokenReplacementWarning(!response.tokenReplacementSuccess);

          // Check if it's a GenAI template and open the modal
          if (response.isGenAITemplate) {
            setShowGenerativeAI(true);
            setIsSuggestEdit(true);
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to load initial template:", error);
      } finally {
        setIsInsertingTemplate(false);
      }
    };

    loadInitialTemplate();
    // Only run when these values are first available
    // The ref ensures it only executes once even if dependencies change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, vinDealerId, currentConversationType]);

  const insertTemplate = async (
    templateId: number,
    isGenAITemplate: boolean
  ) => {
    if (!vinDealerId || !currentConversationType) {
      return;
    }

    setIsInsertingTemplate(true);
    try {
      const request: GetTemplateContentRequest = {
        dealerId: vinDealerId,
        conversationType: currentConversationType,
        templateId,
        consumerId: customerId,
        consumerRecordSystem: customerSystem
      };

      if (autoLeadId) {
        request.leadId = parseInt(autoLeadId, 10);
      }

      const response = await getTemplateContent(request, env);
      if (response && Object.keys(response).length > 0) {
        // Use channel-aware setter
        setTemplateContent(
          response.body || "",
          response.subject,
          response.head
        );
        setShowTokenReplacementWarning(!response.tokenReplacementSuccess);

        if (isGenAITemplate) {
          setShowGenerativeAI(true);
          setIsSuggestEdit(true);
        }
      }

      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to fetch template content:", error);
      return false;
    } finally {
      setIsInsertingTemplate(false);
    }
  };

  return {
    templateData,
    isLoadingTemplates,
    isInsertingTemplate,
    showTokenReplacementWarning,
    setShowTokenReplacementWarning,
    insertTemplate
  };
}
